/**
 * Typed client for the worker API.
 *
 * Every call goes to a relative URL, so the same code works behind the Vite
 * proxy and behind Workers Static Assets. Owner routes take the calendar id so
 * the client can attach `Authorization: Bearer <ownerToken>` from local
 * storage; a session cookie travels alongside it and either one is enough.
 */
import type {
  AdminStats,
  CalEvent,
  Calendar,
  CalendarBundle,
  FloatPin,
  IcsAlarm,
  Invite,
  MeResponse,
  PendingEntry,
  SharedBundle,
  Source,
  StyleTokens,
  Tag,
  User,
  View,
  ViewCamera,
  ViewLayout,
  Visibility,
} from '@circle/shared';
import { ApiError, safeStorage, toApiError } from '../core/api';
import type { PinApi } from '../core/pins';
import { ownerTokens } from './owner-tokens';

export { ApiError, toApiError } from '../core/api';
// One definition: the fragments draw photos through it, and so does the chrome.
export { imageUrl } from '../fragments/default/avatar';

interface RequestOptions {
  method?: string;
  /** JSON body. */
  json?: unknown;
  /** Multipart body; sent as-is, without a content-type header. */
  form?: FormData;
  /** Calendar whose owner token should be attached, when one is stored. */
  calendarId?: string;
  /** A token to send as-is — the admin token. Wins over `calendarId`. */
  bearer?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers();
  const token = options.bearer ?? (options.calendarId ? ownerTokens.get(options.calendarId) : undefined);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (options.json !== undefined) headers.set('content-type', 'application/json');

  let res: Response;
  try {
    res = await fetch(path, {
      method: options.method ?? (options.json !== undefined || options.form ? 'POST' : 'GET'),
      headers,
      credentials: 'include',
      ...(options.json !== undefined ? { body: JSON.stringify(options.json) } : {}),
      ...(options.form ? { body: options.form } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'OFFLINE', 'Could not reach the server. Check your connection.');
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export interface UploadedImage {
  key: string;
  contentType: string;
  bytes: number;
}

/**
 * `POST /api/sources/:id/refresh`. The counts are the worker's `ImportSummary`:
 * what was inserted, what changed, what the feed no longer carries, what came
 * back after being orphaned, how many VEVENTs were parsed, and whether the
 * feed was cut at the cap. A refresh that reached the worker and failed
 * upstream still answers 200, with `source.lastError` set.
 */
export interface RefreshResult {
  source: Source;
  inserted: number;
  updated: number;
  orphaned: number;
  restored: number;
  parsed: number;
  truncated: boolean;
}

export interface CollectInfo {
  calendarName: string;
  sourceName: string;
}

export interface CreateEventBody {
  kind: CalEvent['kind'];
  month: number;
  day: number;
  year?: number;
  title: string;
  imageKey?: string;
  style?: CalEvent['style'];
  layout?: CalEvent['layout'];
  /** The person's own weight, multiplied onto their circles'. [0.25, 4]. */
  prominence?: number;
}

/** A PATCH may clear `year` / `imageKey` by sending null. */
export type PatchEventBody = Partial<Omit<CreateEventBody, 'year' | 'imageKey'>> & {
  year?: number | null;
  imageKey?: string | null;
};

/**
 * POST /api/calendars/:id/tags. `parentId` absent or null makes a circle at the
 * top level, under the implicit root.
 */
export interface CreateTagBody {
  name: string;
  parentId?: string | null;
  colour?: string;
  style?: StyleTokens;
  prominence?: number;
  sort?: number;
}

/**
 * PATCH /api/tags/:id. `colour: null` and `style: null` clear an override —
 * "inherit" in the panel — and `parentId: null` reparents to the root.
 */
export type PatchTagBody = Partial<Omit<CreateTagBody, 'colour' | 'style'>> & {
  colour?: string | null;
  style?: StyleTokens | null;
};

/** POST /api/calendars/:id/views. */
export interface CreateViewBody {
  name: string;
  layout: ViewLayout;
  camera?: ViewCamera;
}

/** PATCH /api/views/:id. `camera: null` forgets the saved camera. */
export type PatchViewBody = Partial<Omit<CreateViewBody, 'camera'>> & {
  camera?: ViewCamera | null;
  sort?: number;
};

/** What `POST /api/calendars/:id/layout/reset` will clear. */
export type LayoutResetScope = 'floats' | 'rings' | 'all';

export interface CreateIcsSource {
  kind: 'ics';
  name: string;
  colour: string;
  url: string;
  refreshHours?: number;
}

export interface CreateCollectSource {
  kind: 'collect';
  name: string;
  colour: string;
}

/**
 * What the worker will store: photos live in D1, so they have to be small.
 * The picker resizes to a 512 px square first (see lib/resize-image), and this
 * is the ceiling that resize has to come in under.
 */
export const MAX_IMAGE_BYTES = 192 * 1024;

/**
 * Whether this browser has ever signed in.
 *
 * The session cookie is HttpOnly, so the SPA cannot read it and used to ask
 * `/api/me` on every load — which, for the great majority of visitors who have
 * no account at all, meant a 401 and a red line in the console every time. The
 * hint is set when a sign-in succeeds and cleared when one ends or when the
 * server says the session is gone; with no hint the call is simply not made and
 * the answer is "signed out", which is right and costs nothing. It is a hint,
 * not a credential: it grants nothing, and the server is still the authority.
 */
export const SIGNED_IN_HINT = 'circle:signed-in';

export const signInHint = {
  present(): boolean {
    try {
      return safeStorage()?.getItem(SIGNED_IN_HINT) === '1';
    } catch {
      return false;
    }
  },
  set(): void {
    try {
      safeStorage()?.setItem(SIGNED_IN_HINT, '1');
    } catch {
      /* private mode */
    }
  },
  clear(): void {
    try {
      safeStorage()?.removeItem(SIGNED_IN_HINT);
    } catch {
      /* private mode */
    }
  },
};

export const api = {
  /** Resolves to null when signed out, so callers do not have to catch a 401. */
  async me(): Promise<MeResponse | null> {
    if (!signInHint.present()) return null;
    try {
      return await request<MeResponse>('/api/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // The hint outlived the session; stop asking.
        signInHint.clear();
        return null;
      }
      throw err;
    }
  },

  /** Rename yourself. The only field an account has of its own. */
  patchMe: (name: string): Promise<{ user: User }> =>
    request('/api/me', { method: 'PATCH', json: { name } }),

  /** Close the account. Refuses with `OWNS_CALENDARS` while any are attached. */
  async deleteMe(): Promise<{ ok: true }> {
    const result = await request<{ ok: true }>('/api/me', { method: 'DELETE' });
    signInHint.clear();
    return result;
  },

  async logout(): Promise<{ ok: true }> {
    try {
      return await request<{ ok: true }>('/auth/logout', { method: 'POST' });
    } finally {
      // Whatever the server made of it, this browser is done being signed in.
      signInHint.clear();
    }
  },

  /**
   * `inviteCode` is whatever the person typed or pasted — the worker
   * normalises it. Omitted where the deployment has no gate, or where the
   * actor is already past it.
   */
  createCalendar: (name: string, inviteCode?: string): Promise<Calendar> =>
    request('/api/calendars', { json: inviteCode ? { name, inviteCode } : { name } }),

  /** The codes this account has minted from its allowance. */
  listInvites: (): Promise<Invite[]> => request('/api/me/invites'),

  mintInvite: (): Promise<Invite> => request('/api/me/invites', { method: 'POST' }),

  revokeInvite: (id: string): Promise<Invite> =>
    request(`/api/me/invites/${id}/revoke`, { method: 'POST' }),

  getCalendar: (id: string, signal?: AbortSignal): Promise<CalendarBundle> =>
    request(`/api/calendars/${id}`, { calendarId: id, ...(signal ? { signal } : {}) }),

  patchCalendar: (
    id: string,
    patch: {
      name?: string;
      visibility?: Visibility;
      icsAlarm?: IcsAlarm;
      ownerTokenEnabled?: boolean;
      theme?: Calendar['theme'];
    },
  ): Promise<Calendar> => request(`/api/calendars/${id}`, { method: 'PATCH', json: patch, calendarId: id }),

  deleteCalendar: (id: string): Promise<{ ok: true }> =>
    request(`/api/calendars/${id}`, { method: 'DELETE', calendarId: id }),

  claimCalendar: (id: string): Promise<Calendar> =>
    request(`/api/calendars/${id}/claim`, { method: 'POST', calendarId: id }),

  rotateOwnerToken: (id: string): Promise<Calendar> =>
    request(`/api/calendars/${id}/owner-token/rotate`, { method: 'POST', calendarId: id }),

  share: (id: string, visibility: Visibility, rotate = false): Promise<Calendar> =>
    request(`/api/calendars/${id}/share`, { json: { visibility, rotate }, calendarId: id }),

  rotateIcsToken: (id: string): Promise<Calendar> =>
    request(`/api/calendars/${id}/ics-token/rotate`, { method: 'POST', calendarId: id }),

  /**
   * `scope` says what goes: `floats` the pinned cards, `rings` the manually
   * placed rings in one view, `all` both. Omitting it keeps the old behaviour,
   * which is what every existing caller wants.
   */
  resetLayout: (
    id: string,
    options: { scope?: LayoutResetScope; viewId?: string } = {},
  ): Promise<{ ok: true }> => {
    const query = new URLSearchParams();
    if (options.scope) query.set('scope', options.scope);
    if (options.viewId) query.set('viewId', options.viewId);
    const suffix = query.size ? `?${query}` : '';
    return request(`/api/calendars/${id}/layout/reset${suffix}`, { method: 'POST', calendarId: id });
  },

  // --- circles -------------------------------------------------------------
  //
  // The bundle already carries `tags` and `views`, so there is no list call
  // here: a change goes out, and the page refetches the bundle when the answer
  // could touch more than the row it edited.

  createTag: (calendarId: string, body: CreateTagBody): Promise<Tag> =>
    request(`/api/calendars/${calendarId}/tags`, { json: body, calendarId }),

  patchTag: (calendarId: string, tagId: string, patch: PatchTagBody): Promise<Tag> =>
    request(`/api/tags/${tagId}`, { method: 'PATCH', json: patch, calendarId }),

  /**
   * A circle's own read-only link: `enabled` mints or withdraws it, `rotate`
   * replaces it. Independent of the calendar's share link.
   */
  shareTag: (calendarId: string, tagId: string, enabled: boolean, rotate = false): Promise<Tag> =>
    request(`/api/tags/${tagId}/share`, { json: { enabled, rotate }, calendarId }),

  /** The worker lifts the circle's children to its parent rather than dropping them. */
  deleteTag: (calendarId: string, tagId: string): Promise<{ ok: true }> =>
    request(`/api/tags/${tagId}`, { method: 'DELETE', calendarId }),

  /** The whole set, not a delta: an empty array puts the entry back on the root alone. */
  setEventTags: (calendarId: string, eventId: string, tagIds: string[]): Promise<CalEvent> =>
    request(`/api/events/${eventId}/tags`, { method: 'PUT', json: { tagIds }, calendarId }),

  patchSource: (
    calendarId: string,
    sourceId: string,
    patch: { defaultTagId?: string | null },
  ): Promise<Source> =>
    request(`/api/sources/${sourceId}`, { method: 'PATCH', json: patch, calendarId }),

  createView: (calendarId: string, body: CreateViewBody): Promise<View> =>
    request(`/api/calendars/${calendarId}/views`, { json: body, calendarId }),

  patchView: (calendarId: string, viewId: string, patch: PatchViewBody): Promise<View> =>
    request(`/api/views/${viewId}`, { method: 'PATCH', json: patch, calendarId }),

  /** Refused with `VIEW_LAST` while it is the only one. */
  deleteView: (calendarId: string, viewId: string): Promise<{ ok: true }> =>
    request(`/api/views/${viewId}`, { method: 'DELETE', calendarId }),

  setDefaultView: (calendarId: string, viewId: string): Promise<View> =>
    request(`/api/views/${viewId}/default`, { method: 'POST', calendarId }),

  createEvent: (calendarId: string, body: CreateEventBody): Promise<CalEvent> =>
    request(`/api/calendars/${calendarId}/events`, { json: body, calendarId }),

  patchEvent: (calendarId: string, eventId: string, patch: PatchEventBody): Promise<CalEvent> =>
    request(`/api/events/${eventId}`, { method: 'PATCH', json: patch, calendarId }),

  deleteEvent: (calendarId: string, eventId: string): Promise<{ ok: true }> =>
    request(`/api/events/${eventId}`, { method: 'DELETE', calendarId }),

  createSource: (calendarId: string, body: CreateIcsSource | CreateCollectSource): Promise<Source> =>
    request(`/api/calendars/${calendarId}/sources`, { json: body, calendarId }),

  refreshSource: (calendarId: string, sourceId: string): Promise<RefreshResult> =>
    request(`/api/sources/${sourceId}/refresh`, { method: 'POST', calendarId }),

  rotateCollectToken: (calendarId: string, sourceId: string): Promise<Source> =>
    request(`/api/sources/${sourceId}/collect-token/rotate`, { method: 'POST', calendarId }),

  deleteSource: (calendarId: string, sourceId: string, keepEvents: boolean): Promise<{ ok: true }> =>
    request(`/api/sources/${sourceId}?keep=${keepEvents ? 'true' : 'false'}`, {
      method: 'DELETE',
      calendarId,
    }),

  listPending: (calendarId: string): Promise<PendingEntry[]> =>
    request(`/api/calendars/${calendarId}/pending`, { calendarId }),

  actOnPending: (
    calendarId: string,
    pendingId: string,
    action: 'approve' | 'reject',
  ): Promise<{ ok: true; action: string; event?: CalEvent }> =>
    request(`/api/pending/${pendingId}`, { method: 'PATCH', json: { action }, calendarId }),

  /** The calendar is named so the photo counts against that ring's quota. */
  uploadImage: (file: File, calendarId?: string): Promise<UploadedImage> => {
    const form = new FormData();
    form.append('file', file);
    const path = calendarId ? `/api/images?calendarId=${encodeURIComponent(calendarId)}` : '/api/images';
    return request(path, { form, ...(calendarId ? { calendarId } : {}) });
  },

  getShared: (token: string, signal?: AbortSignal): Promise<SharedBundle> =>
    request(`/api/shared/${token}`, signal ? { signal } : {}),

  collectInfo: (token: string, signal?: AbortSignal): Promise<CollectInfo> =>
    request(`/api/collect/${token}`, signal ? { signal } : {}),

  collectSubmit: (
    token: string,
    body: { title: string; month: number; day: number; year?: number; imageKey?: string },
  ): Promise<{ ok: true }> => request(`/api/collect/${token}`, { json: body }),

  collectImage: (token: string, file: File): Promise<UploadedImage> => {
    const form = new FormData();
    form.append('file', file);
    return request(`/api/collect/${token}/image`, { form });
  },
};

/**
 * The pin writes for one calendar, over the same `request` as everything else,
 * in the shape `RemotePinStore` (core/pins.ts) asks for. The core never imports
 * this file; the page hands the store the object.
 */
export function pinApi(calendarId: string): PinApi {
  return {
    calendarId,
    setEventLayout: (eventId: string, layout: FloatPin | null) =>
      request(`/api/events/${encodeURIComponent(eventId)}`, { method: 'PATCH', json: { layout }, calendarId }),
    setEventPin: (eventId: string, tagId: string, pin: FloatPin | null) =>
      request(`/api/events/${encodeURIComponent(eventId)}/pins/${encodeURIComponent(tagId)}`, {
        method: 'PUT',
        json: pin,
        calendarId,
      }),
    resetLayout: () => api.resetLayout(calendarId),
  };
}

/**
 * The operator's API, behind the admin token rather than any session. The
 * token is handed in on every call: the page that uses this keeps it for the
 * tab and nowhere else.
 */
export const adminApi = {
  stats: (token: string): Promise<AdminStats> => request('/api/admin/stats', { bearer: token }),

  listInvites: (token: string): Promise<Invite[]> => request('/api/admin/invites', { bearer: token }),

  mintInvites: (
    token: string,
    body: { count?: number; maxUses?: number; expiresInDays?: number; note?: string },
  ): Promise<Invite[]> => request('/api/admin/invites', { bearer: token, json: body }),

  revokeInvite: (token: string, id: string): Promise<Invite> =>
    request(`/api/admin/invites/${id}/revoke`, { method: 'POST', bearer: token }),
};
